/**
 * CalendarMonthCarousel
 *
 * Month calendar with arrow navigation and horizontal swipe support.
 * Renders a single Calendar instance that updates in place — no
 * multi-page scroll, so month transitions are flicker-free.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  type GestureResponderEvent,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Calendar, type DateData } from 'react-native-calendars';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';

import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

const SCREEN_WIDTH = Dimensions.get('window').width;
const MODAL_HORIZONTAL_PADDING = Spacing.lg * 2;
const CALENDAR_HORIZONTAL_PADDING = 8;
const CALENDAR_WIDTH = Math.min(SCREEN_WIDTH - MODAL_HORIZONTAL_PADDING, 360);
const INNER_WIDTH = CALENDAR_WIDTH - CALENDAR_HORIZONTAL_PADDING * 2;
const DAY_CELL_WIDTH = Math.floor(INNER_WIDTH / 7);
const SWIPE_THRESHOLD = 50;

/** Get today's date as YYYY-MM-DD */
function getTodayString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

type CalendarMonthCarouselProps = {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  accentColor: string;
  palette: typeof Colors.light;
  minDate?: string;
};

/** Add or subtract months from a YYYY-MM-DD string */
function shiftMonth(dateString: string, delta: number): string {
  const [year, month] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

/** Get YYYY-MM-01 from a date string */
function getMonthStart(dateString: string): string {
  return dateString.substring(0, 7) + '-01';
}

// ---- Month Navigation Header ----

type MonthNavigationHeaderProps = {
  centerMonth: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  accentColor: string;
  palette: typeof Colors.light;
};

function MonthNavigationHeader({
  centerMonth,
  onPrev,
  onNext,
  onToday,
  accentColor,
  palette,
}: MonthNavigationHeaderProps) {
  const label = useMemo(() => {
    const [year, month] = centerMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [centerMonth]);

  return (
    <View style={styles.headerContainer}>
      <View style={styles.navRow}>
        <TouchableOpacity onPress={onPrev} style={styles.arrowButton}>
          <Ionicons name="chevron-back" size={22} color={accentColor} />
        </TouchableOpacity>

        <Text
          style={[
            styles.headerLabel,
            { color: palette.text, fontFamily: getBodyFont() },
          ]}
        >
          {label}
        </Text>

        <TouchableOpacity onPress={onNext} style={styles.arrowButton}>
          <Ionicons name="chevron-forward" size={22} color={accentColor} />
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        onPress={onToday}
        style={[styles.todayButton, { borderColor: accentColor }]}
        activeOpacity={0.7}
      >
        <Text
          style={[
            styles.todayLabel,
            { color: accentColor, fontFamily: getBodyFont() },
          ]}
        >
          {new Date().getDate()}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

// ---- Main Component ----

export function CalendarMonthCarousel({
  selectedDate,
  onDateSelect,
  accentColor,
  palette,
  minDate,
}: CalendarMonthCarouselProps) {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';
  const [centerMonth, setCenterMonth] = useState(() => getMonthStart(selectedDate));
  const isAnimating = useRef(false);

  // Fade animation
  const calendarOpacity = useSharedValue(1);
  const fadeStyle = useAnimatedStyle(() => ({ opacity: calendarOpacity.value }));

  const setAnimatingDone = useCallback(() => {
    isAnimating.current = false;
  }, []);

  const applyShiftAndFadeIn = useCallback((delta: number) => {
    setCenterMonth(prev => shiftMonth(prev, delta));
    calendarOpacity.value = withTiming(1, { duration: 120 }, () => {
      runOnJS(setAnimatingDone)();
    });
  }, [calendarOpacity, setAnimatingDone]);

  const navigateMonth = useCallback((delta: number) => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    calendarOpacity.value = withTiming(0, { duration: 80 }, (finished) => {
      if (finished) {
        runOnJS(applyShiftAndFadeIn)(delta);
      }
    });
  }, [applyShiftAndFadeIn, calendarOpacity]);

  const handleToday = useCallback(() => {
    const today = getTodayString();
    onDateSelect(today);
    setCenterMonth(getMonthStart(today));
  }, [onDateSelect]);

  // Track horizontal swipe via raw touch events
  const touchStartX = useRef(0);

  const handleTouchStart = useCallback((e: GestureResponderEvent) => {
    touchStartX.current = e.nativeEvent.pageX;
  }, []);

  const handleTouchEnd = useCallback((e: GestureResponderEvent) => {
    const dx = e.nativeEvent.pageX - touchStartX.current;
    if (dx > SWIPE_THRESHOLD) {
      navigateMonth(-1);
    } else if (dx < -SWIPE_THRESHOLD) {
      navigateMonth(1);
    }
  }, [navigateMonth]);

  const handleDayPress = useCallback(
    (day: DateData) => {
      onDateSelect(day.dateString);
    },
    [onDateSelect],
  );

  const calendarTheme = useMemo(() => {
    return {
      backgroundColor: palette.cardBackground,
      calendarBackground: palette.cardBackground,
      textSectionTitleColor: palette.textMuted,
      selectedDayBackgroundColor: accentColor,
      selectedDayTextColor: '#FFFFFF',
      todayTextColor: accentColor,
      dayTextColor: palette.text,
      textDisabledColor: isDark
        ? 'rgba(255, 255, 255, 0.25)'
        : 'rgba(26, 26, 26, 0.25)',
      monthTextColor: palette.text,
      textMonthFontWeight: '600' as const,
      textDayFontSize: 16,
      textMonthFontSize: 18,
      textDayHeaderFontSize: 13,
      arrowColor: accentColor,
      'stylesheet.calendar.header': {
        week: {
          marginTop: 0,
          marginBottom: 0,
          flexDirection: 'row',
          justifyContent: 'flex-start',
        },
        dayHeader: {
          width: DAY_CELL_WIDTH,
          textAlign: 'center',
          fontSize: 13,
          color: palette.textMuted,
        },
        dayTextAtIndex0: { color: palette.textMuted },
        dayTextAtIndex1: { color: palette.textMuted },
        dayTextAtIndex2: { color: palette.textMuted },
        dayTextAtIndex3: { color: palette.textMuted },
        dayTextAtIndex4: { color: palette.textMuted },
        dayTextAtIndex5: { color: palette.textMuted },
        dayTextAtIndex6: { color: palette.textMuted },
      },
      'stylesheet.calendar.main': {
        week: {
          marginTop: 0,
          marginBottom: 0,
          flexDirection: 'row',
          justifyContent: 'flex-start',
        },
        dayContainer: {
          width: DAY_CELL_WIDTH,
          alignItems: 'center',
        },
      },
      'stylesheet.day.basic': {
        base: {
          width: DAY_CELL_WIDTH,
          height: 38,
          alignItems: 'center',
          justifyContent: 'center',
        },
        text: { fontSize: 16, color: palette.text },
      },
    };
  }, [palette, accentColor, isDark]);

  const markedDates = useMemo(
    () => ({
      [selectedDate]: {
        selected: true,
        selectedColor: accentColor,
      },
    }),
    [selectedDate, accentColor],
  );

  return (
    <View style={styles.container}>
      <MonthNavigationHeader
        centerMonth={centerMonth}
        onPrev={() => navigateMonth(-1)}
        onNext={() => navigateMonth(1)}
        onToday={handleToday}
        accentColor={accentColor}
        palette={palette}
      />

      <Animated.View
        style={[styles.calendarPage, fadeStyle]}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Calendar
          key={centerMonth}
          current={centerMonth}
          firstDay={1}
          markedDates={markedDates}
          onDayPress={handleDayPress}
          minDate={minDate}
          theme={calendarTheme as any}
          hideArrows
          hideExtraDays={false}
          disableMonthChange
          renderHeader={() => <View />}
          style={styles.calendar}
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: CALENDAR_WIDTH,
    paddingHorizontal: CALENDAR_HORIZONTAL_PADDING,
  },
  calendarPage: {
    width: INNER_WIDTH,
  },
  calendar: {
    width: INNER_WIDTH,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.sm,
    height: 44,
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  arrowButton: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerLabel: {
    fontSize: 18,
    fontWeight: '600',
  },
  todayButton: {
    width: 30,
    height: 30,
    borderRadius: 7,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  todayLabel: {
    fontSize: 14,
    fontWeight: '700',
  },
});
